// QUICK ADD (spec §113): one floating menu for the things created on the road — client, lead, policy,
// task, appointment, claim, document upload, note — and (ideas track 43) a business card. Every entry opens the same forms as everywhere else.
import { useEffect, useRef, useState } from 'react';
import { useRuntime } from '../core/runtime.tsx';
import { Field, Modal, PersonPicker } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, useRoute } from '../core/router.ts';
import { openers } from './forms.tsx';
import { ClaimForm } from './service.tsx';
import { CardScan } from './card-scan.tsx';

/** Ask for the client first, then continue (e.g. "Add policy" from anywhere). */
export function PickPersonThen({ then, titleKey = 'quickadd.pick_person' }: { then: (personId: string) => void; titleKey?: string }) {
  const rt = useRuntime();
  const [pid, setPid] = useState<string | null>(null);
  return (
    <Modal title={rt.t(titleKey)} footer={<><button className="btn" onClick={rt.close}>{rt.t('common.cancel')}</button><button className="btn btn-primary" disabled={!pid} onClick={() => { rt.close(); setTimeout(() => then(pid!), 0); }} data-testid="pick-person-next">{rt.t('common.next')}</button></>}>
      <Field label={rt.t('common.person')} group><PersonPicker value={pid} onChange={setPid} autoFocus /></Field>
    </Modal>
  );
}

export function QuickAdd({ variant }: { variant: 'top' | 'fab' }) {
  const rt = useRuntime();
  const { t } = rt;
  const route = useRoute();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const here = route.parts[0] === 'people' ? route.parts[1] ?? null : null;
  const o = openers(rt);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', close); window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('mousedown', close); window.removeEventListener('keydown', esc); };
  }, [open]);
  const items: [string, string, () => void][] = [
    ['quickadd.note', 'note', () => o.note(here)],
    ['quickadd.client', 'people', () => o.person()],
    ['quickadd.lead', 'prospects', () => navigate('/prospects')],
    ['quickadd.card', 'upload', () => rt.open(<CardScan />)],
    ['quickadd.policy', 'policy', () => (here ? o.policy(here) : rt.open(<PickPersonThen then={(pid) => o.policy(pid)} />))],
    ['quickadd.task', 'task', () => o.followup(here)],
    ['quickadd.appointment', 'appointment', () => o.appointment(here)],
    ['quickadd.claim', 'shield', () => rt.open(<ClaimForm personId={here} />)],
    ['quickadd.upload', 'upload', () => navigate(`/vault?upload=1${here ? `&person=${here}` : ''}`)],
  ];
  return (
    <div className={`quickadd ${variant}`} ref={ref}>
      <button className={variant === 'fab' ? 'fab' : 'btn btn-primary'} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} data-testid={variant === 'fab' ? 'fab' : 'quickadd'} aria-label={t('quickadd.title')}>
        <Icon name="plus" />{variant === 'top' && <span>{t('quickadd.title')}</span>}
      </button>
      {open && <div className="quickadd-menu glass" role="menu">{items.map(([k, icon, fn]) => <button key={k} role="menuitem" onClick={() => { setOpen(false); fn(); }} data-testid={`qa-${k.split('.')[1]}`}><Icon name={icon} /><span>{t(k)}</span></button>)}</div>}
    </div>
  );
}
